// drop in replacement for ReactDatepicker.
// uses ReactDatepicker on desktop computers
// and a datetime-local input on mobiles

import { TextField } from '@mui/material'
import { DatePicker, DateTimePicker } from '@mui/x-date-pickers'
import clsx from 'clsx'
import { useEffect, useState } from 'react'
import { BrowserView, MobileView } from 'react-device-detect'
import { useTranslation, withTranslation } from 'react-i18next'
import ITranslationProps from '../../types/translationProps'
import { toDateString, toDatetimeLocal } from '../../util'
import DateUtil from '../../util/DateUtil'

export type DateInputMode = 'date' | 'datetime' | 'time'

interface IProps extends ITranslationProps {
  name?: string
  mode?: DateInputMode
  dateFormat?: string
  placeholderText?: string
  className?: string
  selected: Date
  timeCaption?: string
  onChange?: (date?: Date) => void
  required?: boolean
  minDate?: Date
  maxDate?: Date
  isClearable?: boolean
  disabled?: boolean
  label?: string
}

function getNativeInputType(mode: DateInputMode) {
  switch (mode) {
    case 'datetime':
      return 'datetime-local'
    case 'time':
      return 'time'
    default:
      return 'date'
  }
}

function getNativeValue(mode: DateInputMode, value: Date) {
  switch (mode) {
    case 'datetime':
      return toDatetimeLocal(value)
    case 'time':
      return DateUtil.formatTime(value)
    default:
      return toDateString(value)
  }
}

// the value of a native input as a date; a time ("17:00") is put on the day of `base` (or today),
// new Date("17:00") would be an invalid date
export function parseNativeValue(mode: DateInputMode, value: string, base?: Date | null): Date | null {
  if (!value) return null
  if (mode === 'time') {
    const match = value.match(/^(\d{1,2}):(\d{2})/)
    if (!match) return null
    const date = base && !Number.isNaN(base.getTime()) ? new Date(base) : new Date()
    date.setHours(Number(match[1]), Number(match[2]), 0, 0)
    return date
  }
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? null : date
}

// a time as people type it: 17, 1700, 17:00, 17.00, 17,00, 9:30, 930, "17 Uhr"; null if it is none
export function parseTypedTime(text: string): { hours: number, minutes: number } | null {
  const clean = text.trim().toLowerCase().replace(/\s*(uhr|h)\.?$/, '').trim()
  let match = clean.match(/^(\d{1,2})(?:(?:\s*[:.,]\s*|\s+)(\d{2}))?$/)
  if (!match) match = clean.match(/^(\d{1,2})(\d{2})$/)
  if (!match) return null
  const hours = Number(match[1])
  const minutes = Number(match[2] ?? 0)
  return hours < 24 && minutes < 60 ? { hours, minutes } : null
}

// desktop: a plain field instead of the picker mask ("9:30" became "93:0"); saved when the field is left
function TimeTextInput({ selected, onChange, disabled, label }: {
  selected: Date | null, onChange: (date: Date | null) => void, disabled?: boolean, label?: string
}) {
  const { t } = useTranslation()
  const shown = selected && !Number.isNaN(selected.getTime()) ? DateUtil.formatTime(selected) : ''
  const [text, setText] = useState(shown)
  const [invalid, setInvalid] = useState(false)
  useEffect(() => { setText(shown); setInvalid(false) }, [shown])

  const commit = () => {
    if (!text.trim()) {
      setInvalid(false)
      if (shown) onChange(null)
      return
    }
    const time = parseTypedTime(text)
    if (!time) {
      setInvalid(true)
      return
    }
    const date = selected && !Number.isNaN(selected.getTime()) ? new Date(selected) : new Date()
    date.setHours(time.hours, time.minutes, 0, 0)
    setInvalid(false)
    setText(DateUtil.formatTime(date))
    if (DateUtil.formatTime(date) !== shown) onChange(date)
  }

  return (
    <TextField
      fullWidth
      label={label}
      value={text}
      placeholder={t('timeInput.placeholder')}
      disabled={disabled}
      error={invalid}
      helperText={invalid ? t('timeInput.invalid') : undefined}
      inputProps={{ inputMode: 'numeric', autoComplete: 'off', 'aria-label': label || t('Time') }}
      onChange={(event) => { setText(event.target.value); setInvalid(false) }}
      onBlur={commit}
      onKeyDown={(event) => { if (event.key === 'Enter') commit() }}
    />
  )
}

export default withTranslation()((props: IProps) => {
  const mode = props.mode ?? 'date'
  const onChangeDatetimeLocal = (event: any) => {
    props.onChange(parseNativeValue(mode, event.target.value, props.selected))
  }

  return (
    <>
      <BrowserView>
        {mode === 'date' && (
          <DatePicker
            label={props.label}
            value={props.selected}
            ignoreInvalidInputs
            onChange={(newValue) => {
              props.onChange(newValue as Date)
            }}
            disabled={props.disabled}
            minDate={props.minDate}
            maxDate={props.maxDate}
            renderInput={(params) => <TextField fullWidth placeholder={props.placeholderText} {...params} />}
          />
        )}
        {mode === 'time' && (
          <TimeTextInput selected={props.selected} onChange={props.onChange} disabled={props.disabled} label={props.label} />
        )}
        {mode === 'datetime' && (
          <DateTimePicker
            renderInput={(params) => <TextField fullWidth placeholder={props.placeholderText} {...params} />}
            label={props.label}
            value={props.selected}
            disabled={props.disabled}
            minDate={props.minDate}
            maxDate={props.maxDate}
            ignoreInvalidInputs
            onChange={(newValue) => {
              props.onChange(newValue as Date)
            }}
          />
        )}
      </BrowserView>
      {
        /* Browser support for datetime-local is limited, however the support
           in Google Chrome, Mobile Safari and Firefox Mobile is good enough */
      }
      <MobileView>
        <div className="control has-icons-left has-icons-right">
          <input
            type={getNativeInputType(mode)}
            name={props.name}
            className={clsx('input', props.className)}
            value={props.selected
              ? getNativeValue(mode, props.selected)
              : null}
            onChange={onChangeDatetimeLocal}
            required={props.required}
            disabled={props.disabled}
            autoComplete="off"
          />
          <span className="icon is-right">
            <i className="fas fa-calendar" />
          </span>
        </div>
      </MobileView>
    </>
  )
})
